import { ApiError } from "../api/client";
import { userManager } from "./oidc-config";

/**
 * CHP-419: clean end-of-session handling.
 *
 * When a session genuinely ends — the access token expired and silent renew
 * could not refresh it (the refresh token lapsed/was revoked, or Authentik was
 * unreachable) — the user must be signed out cleanly instead of left on a page
 * where every request 401s. Two paths feed this single exit:
 *   - proactive: oidc-client-ts fires `accessTokenExpired` once the token is
 *     dead and renew did not save it (installSessionExpiryListeners, below);
 *   - reactive: any API call returns 401 (wired in App.tsx's query/mutation
 *     cache).
 * Both funnel through endSession() so the behaviour is identical and fires once.
 *
 * Sign-out is local-only per ADR-0010 (the browser holds no Authentik session
 * cookie, so there is no end-session round-trip) — matches useCurrentUser.signOut.
 */

// Once we've begun ending the session, ignore further triggers until the
// navigation completes. Without this, the proactive event and a concurrent 401
// (or several in-flight 401s) would each try to sign out and bounce.
let ending = false;

export async function endSession(): Promise<void> {
  if (ending) return;
  // Don't bounce when we're already on an auth surface: the login page itself,
  // the OIDC callback mid-exchange (a transient 401 there is sign-in noise,
  // not a dead session), or the public set-password/forgot-password pages —
  // there a 401 is a HANDLED response (expired or replayed link, CHP-1195)
  // that the page renders as its own message; hard-navigating to
  // /login?expired=1 pre-empts it and reads as a silent bounce (the UAT
  // AC-B-02 symptom). Without this guard a 401 during sign-in would loop.
  const path = window.location.pathname;
  if (
    path.startsWith("/login") ||
    path.startsWith("/auth/") ||
    path.startsWith("/set-password") ||
    path.startsWith("/forgot-password")
  ) {
    return;
  }
  ending = true;
  try {
    await userManager.removeUser();
  } finally {
    // Hard navigation (not the router) so all in-memory React/Query state is
    // discarded. The `expired` flag lets the login page explain what happened.
    window.location.assign("/login?expired=1");
  }
}

/**
 * Attach the proactive expiry listeners to the shared UserManager. Call once at
 * app startup (main.tsx, outside StrictMode so it registers exactly once).
 */
export function installSessionExpiryListeners(): void {
  // The access token expired and automaticSilentRenew did not save it — end the
  // session cleanly even if the user is idle, so they get the redirect + message
  // rather than discovering a dead page on their next click.
  userManager.events.addAccessTokenExpired(() => {
    void endSession();
  });
  // A single renew failure can be transient (network blip) while the current
  // access token is still valid, so don't sign out here — just surface it for
  // diagnosis. If the token then truly lapses, accessTokenExpired drives the
  // sign-out; a failed API call drives the reactive path (App.tsx).
  userManager.events.addSilentRenewError((error) => {
    console.warn("OIDC silent renew failed:", error);
  });
}

/**
 * Reactive path: a 401 from any query or mutation means the access token is
 * dead, so end the session cleanly rather than letting the user keep hitting a
 * page where every action errors. Wired into TanStack Query's query/mutation
 * cache onError in App.tsx (the one place every API call funnels through —
 * apps/web/src/api/client.ts throws ApiError from ~9 call-sites, so there is no
 * single fetch chokepoint).
 *
 * Only 401 triggers it: a 403 (e.g. `tc_required`) is a real, handled response,
 * not a dead session, and must pass through untouched. endSession() itself is
 * idempotent and route-guarded, so a 401 on the login page (wrong password) or
 * during the OIDC callback is a no-op.
 */
export function endSessionOnUnauthorized(error: unknown): void {
  if (error instanceof ApiError && error.status === 401) {
    void endSession();
  }
}
